// ============================================================================
// CompetencyCardsSection (mig 264) — carte « Cartes de competence » de l'Apercu
// ============================================================================
// DECISION-2026-10-19 : la carte de competence se saisit UNE fois ici, sur la
// fiche de la personne ; ZoneSecur la lit. Le composant charge SA route
// (/api/competency-cards) : un refus serveur (403) ne rend RIEN — la carte
// n'existe pas pour qui n'a pas le droit, elle n'est pas « vide ».
//
// Le numero (sensibilite HAUTE) n'arrive que si le serveur l'accorde ; sinon
// `hasCardNumber` permet d'afficher « masque » sans la valeur. Les gestes
// (ajouter, modifier, retirer, reviser) ne s'offrent que si `can.manage`, et le
// serveur re-verifie chacun.

import { useState } from 'react';
import { BadgeCheck, Check, IdCard, Pencil, Plus, Trash2, X } from 'lucide-react';
import { ConfirmDialog, EmptyState, GS_COMPONENTS, StatusBadge } from '@gs/ui';
import { ApiForbiddenError } from '@gs/api-client';
import { useTranslation } from '../../i18n';
import {
  COMPETENCY_CARD_TYPES,
  cardStanding,
  type CardStanding,
  type CompetencyCardType,
} from '../../lib/profile/competencyCardTypes';
import {
  competencyErrorCode,
  useAddCompetencyCard,
  useCompetencyCards,
  useRetireCompetencyCard,
  useReviewCompetencyCard,
  useUpdateCompetencyCard,
  type CompetencyCard,
  type CompetencyCardDraft,
} from '../../hooks/useCompetencyCards';

const STANDING_VARIANT: Record<CardStanding, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'danger',
  pending: 'info',
  rejected: 'neutral',
};

const LABEL = 'text-meta font-medium text-slate-600 dark:text-slate-300';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Formulaire (ajout et modification) ─────────────────────────────────────

function CardForm({
  initial,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  initial: CompetencyCardDraft;
  pending: boolean;
  error: unknown;
  onSubmit: (draft: CompetencyCardDraft) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [type, setType] = useState<CompetencyCardType>(initial.cardTypeCode);
  const [number, setNumber] = useState(initial.cardNumber ?? '');
  const [issuedOn, setIssuedOn] = useState(initial.issuedOn ?? '');
  const [expiresOn, setExpiresOn] = useState(initial.expiresOn ?? '');
  const datesInvalid = issuedOn !== '' && expiresOn !== '' && expiresOn < issuedOn;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (datesInvalid) return;
    onSubmit({
      cardTypeCode: type,
      cardNumber: number.trim() === '' ? null : number.trim(),
      issuedOn: issuedOn === '' ? null : issuedOn,
      expiresOn: expiresOn === '' ? null : expiresOn,
    });
  };

  return (
    <form onSubmit={submit} className="mt-3 grid gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-2 dark:border-slate-700">
      <div className="flex flex-col gap-1">
        <label htmlFor="cc-type" className={LABEL}>{t('gest:competencyCards.form.type')}</label>
        <select
          id="cc-type"
          value={type}
          onChange={(e) => setType(e.target.value as CompetencyCardType)}
          className={GS_COMPONENTS.input}
        >
          {COMPETENCY_CARD_TYPES.map((code) => (
            <option key={code} value={code}>{t(`gest:competencyCards.types.${code}`)}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="cc-number" className={LABEL}>{t('gest:competencyCards.form.number')}</label>
        <input
          id="cc-number"
          type="text"
          maxLength={32}
          autoComplete="off"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          className={GS_COMPONENTS.input}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="cc-issued" className={LABEL}>{t('gest:competencyCards.form.issuedOn')}</label>
        <input id="cc-issued" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} className={GS_COMPONENTS.input} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="cc-expires" className={LABEL}>{t('gest:competencyCards.form.expiresOn')}</label>
        <input id="cc-expires" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} className={GS_COMPONENTS.input} />
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <button type="submit" disabled={pending || datesInvalid} className={`${GS_COMPONENTS.btnAction} touch-manipulation disabled:opacity-50`}>
          {t('gest:competencyCards.form.save')}
        </button>
        <button type="button" onClick={onCancel} className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}>
          {t('gest:competencyCards.form.cancel')}
        </button>
        {datesInvalid ? (
          <p role="alert" className="text-meta text-red-700 dark:text-red-400">{t('gest:competencyCards.form.datesInvalid')}</p>
        ) : null}
        {error ? (
          <p role="alert" className="text-meta text-red-700 dark:text-red-400">
            {t(`gest:competencyCards.errors.${competencyErrorCode(error)}`)}
          </p>
        ) : null}
      </div>
    </form>
  );
}

// ─── Section ────────────────────────────────────────────────────────────────

export interface CompetencyCardsSectionProps {
  /** Courriel de la personne ; absent = SES cartes (lecture seule). */
  email?: string;
}

export function CompetencyCardsSection({ email }: CompetencyCardsSectionProps) {
  const { t, i18n } = useTranslation();
  const q = useCompetencyCards(email, true);
  const target = email ?? '';
  const add = useAddCompetencyCard(target);
  const update = useUpdateCompetencyCard(target);
  const retire = useRetireCompetencyCard(target);
  const review = useReviewCompetencyCard(target);
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [retiring, setRetiring] = useState<CompetencyCard | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  // Refus serveur : la carte n'existe pas pour ce lecteur (pas un etat vide).
  if (q.isError && q.error instanceof ApiForbiddenError) return null;
  if (q.isPending) return null;
  if (q.isError) {
    return (
      <p role="alert" className="text-meta text-red-700 dark:text-red-400">
        {t(`gest:competencyCards.errors.${competencyErrorCode(q.error)}`)}
      </p>
    );
  }

  const { cards, can } = q.data;
  const manage = can.manage && email !== undefined;
  const locale = i18n.language.startsWith('en') ? 'en-CA' : 'fr-CA';
  const fmt = (d: string | null) =>
    d ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`)) : '—';
  const today = todayIso();
  const typeLabel = (code: string) =>
    (COMPETENCY_CARD_TYPES as readonly string[]).includes(code) ? t(`gest:competencyCards.types.${code}`) : code;

  const numberCell = (c: CompetencyCard) => {
    if (c.cardNumber) return <span className="font-mono tabular-nums">{c.cardNumber}</span>;
    if (c.hasCardNumber) return <span className="italic text-slate-400">{t('gest:competencyCards.numberHidden')}</span>;
    return '—';
  };

  return (
    <div id="competency-cards" className={`${GS_COMPONENTS.card} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300">
            <IdCard size={18} aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">{t('gest:competencyCards.title')}</h3>
            <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">{t('gest:competencyCards.description')}</p>
          </div>
        </div>
        {manage && editing !== 'new' ? (
          <button type="button" onClick={() => setEditing('new')} className={`${GS_COMPONENTS.btnAction} touch-manipulation`}>
            <Plus size={16} aria-hidden="true" />
            {t('gest:competencyCards.add')}
          </button>
        ) : null}
      </div>

      {manage && editing === 'new' ? (
        <CardForm
          initial={{ cardTypeCode: COMPETENCY_CARD_TYPES[0], cardNumber: null, issuedOn: null, expiresOn: null }}
          pending={add.isPending}
          error={add.error}
          onSubmit={(draft) => add.mutate(draft, { onSuccess: () => setEditing(null) })}
          onCancel={() => { add.reset(); setEditing(null); }}
        />
      ) : null}

      {cards.length === 0 ? (
        <div className="mt-3">
          <EmptyState icon={<BadgeCheck size={40} aria-hidden="true" />} title={t('gest:competencyCards.empty')} />
        </div>
      ) : (
        <ul className="mt-3 flex flex-col">
          {cards.map((c) => {
            const standing = cardStanding(c, today);
            return (
              <li key={c.id} className="border-b border-slate-100 py-3 last:border-b-0 dark:border-slate-700/60">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-body font-medium text-slate-800 dark:text-slate-100">{typeLabel(c.cardTypeCode)}</p>
                    <p className="text-meta text-slate-500 dark:text-slate-400">
                      {t('gest:competencyCards.number')} : {numberCell(c)}
                      {' · '}
                      {t('gest:competencyCards.expiresOn')} : {fmt(c.expiresOn)}
                    </p>
                    {c.reviewStatus === 'rejected' && c.rejectionNote ? (
                      <p className="text-meta text-slate-500 dark:text-slate-400">
                        {t('gest:competencyCards.rejectionNote')} : {c.rejectionNote}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge variant={STANDING_VARIANT[standing]}>{t(`gest:competencyCards.standing.${standing}`)}</StatusBadge>
                    {manage && c.reviewStatus === 'pending' ? (
                      <>
                        <button
                          type="button"
                          aria-label={t('gest:competencyCards.review.approve')}
                          title={t('gest:competencyCards.review.approve')}
                          disabled={review.isPending}
                          onClick={() => review.mutate({ id: c.id, action: 'approve', rejectionNote: null })}
                          className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}
                        >
                          <Check size={16} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label={t('gest:competencyCards.review.reject')}
                          title={t('gest:competencyCards.review.reject')}
                          onClick={() => { setRejecting(c.id); setRejectNote(''); }}
                          className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}
                        >
                          <X size={16} aria-hidden="true" />
                        </button>
                      </>
                    ) : null}
                    {manage ? (
                      <>
                        <button
                          type="button"
                          aria-label={t('gest:competencyCards.edit')}
                          title={t('gest:competencyCards.edit')}
                          onClick={() => setEditing(c.id)}
                          className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}
                        >
                          <Pencil size={16} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label={t('gest:competencyCards.retire.action')}
                          title={t('gest:competencyCards.retire.action')}
                          onClick={() => setRetiring(c)}
                          className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>

                {manage && rejecting === c.id ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <label htmlFor={`cc-reject-${c.id}`} className={LABEL}>{t('gest:competencyCards.review.note')}</label>
                    <textarea
                      id={`cc-reject-${c.id}`}
                      rows={2}
                      maxLength={240}
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      className={GS_COMPONENTS.input}
                    />
                    <div className="flex gap-3">
                      <button
                        type="button"
                        disabled={review.isPending}
                        onClick={() =>
                          review.mutate(
                            { id: c.id, action: 'reject', rejectionNote: rejectNote.trim() === '' ? null : rejectNote.trim() },
                            { onSuccess: () => setRejecting(null) },
                          )
                        }
                        className={`${GS_COMPONENTS.btnDangerOutline} touch-manipulation disabled:opacity-50`}
                      >
                        {t('gest:competencyCards.review.confirmReject')}
                      </button>
                      <button type="button" onClick={() => setRejecting(null)} className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}>
                        {t('gest:competencyCards.form.cancel')}
                      </button>
                    </div>
                  </div>
                ) : null}

                {manage && editing === c.id && (COMPETENCY_CARD_TYPES as readonly string[]).includes(c.cardTypeCode) ? (
                  <CardForm
                    initial={{
                      cardTypeCode: c.cardTypeCode as CompetencyCardType,
                      cardNumber: c.cardNumber,
                      issuedOn: c.issuedOn,
                      expiresOn: c.expiresOn,
                    }}
                    pending={update.isPending}
                    error={update.error}
                    onSubmit={(draft) => update.mutate({ id: c.id, draft }, { onSuccess: () => setEditing(null) })}
                    onCancel={() => { update.reset(); setEditing(null); }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {review.isError ? (
        <p role="alert" className="mt-2 text-meta text-red-700 dark:text-red-400">
          {t(`gest:competencyCards.errors.${competencyErrorCode(review.error)}`)}
        </p>
      ) : null}

      <ConfirmDialog
        open={retiring !== null}
        variant="danger"
        title={t('gest:competencyCards.retire.title')}
        message={t('gest:competencyCards.retire.message', { type: retiring ? typeLabel(retiring.cardTypeCode) : '' })}
        confirmLabel={t('gest:competencyCards.retire.confirm')}
        cancelLabel={t('gest:competencyCards.form.cancel')}
        isLoading={retire.isPending}
        onConfirm={() => { if (retiring) retire.mutate(retiring.id, { onSuccess: () => setRetiring(null) }); }}
        onCancel={() => setRetiring(null)}
      />
    </div>
  );
}
